/**
 * Audio Lessons — client-side transcript view.
 *
 * The episode endpoint returns the baked transcript sidecar as an opaque JSON
 * object (`rendition.transcript`, typed `Record<string, unknown>` in the
 * generated contract). We consume ONLY the fields the audio pipeline documents
 * in `docs/specs/audio-pipeline.spec.md` §6 "Transcript sidecar" — everything
 * else is ignored. This module owns the narrow read-model plus the
 * position→segment/word lookups the karaoke `SyncedTranscript` relies on.
 *
 * Source of the shape: `backend/src/services/audio/TranscriptBuilder.ts`
 * (`Transcript` / `TranscriptSegment` / `TranscriptWord`).
 */

export type TeachingLanguage = 'source' | 'target';

/** One word on the GLOBAL episode timeline (absolute ms). */
export interface TranscriptWord {
  text: string;
  startMs: number;
  endMs: number;
}

/** Bilingual display pair for CONTENT segments (example / repeat / dialogue). */
export interface TranscriptPair {
  /** Native gloss keyed by locale (`es_419` always present; region overrides optional). */
  native: Record<string, string>;
  /** The spoken target-language line. */
  target: string;
}

/** One vocab term + its native gloss for VOCAB segments. */
export interface TranscriptVocabItem {
  target: string;
  source: Record<string, string>;
}

/**
 * One transcript line on the global timeline. `text` is the spoken side (in the
 * rendition's language); the bilingual display payload depends on the segment
 * family: `transcriptPair` (content), `narration` (full bilingual, runs
 * flattened), or `vocab` (term/gloss items).
 */
export interface AudioTranscriptSegment {
  index: number;
  /** Pipeline SegmentType — opaque to the player (e.g. `explanation`, `example`, `vocab`). */
  type: string;
  /** `narrator` | `voiceA` | `voiceB` — opaque to the player. */
  role: string;
  /** Start of the utterance on the global episode timeline, ms. */
  startMs: number;
  /** End of the utterance (before the baked pause), ms. */
  endMs: number;
  /** Silence baked AFTER this segment, ms. */
  pauseAfterMs: number;
  /** The spoken line, in the rendition's language. */
  text: string;
  words: TranscriptWord[];
  /** CONTENT segments: bilingual display pair. */
  transcriptPair?: TranscriptPair;
  /** NARRATION segments: flattened bilingual narration text keyed by locale. */
  narration?: Record<string, string>;
  /** VOCAB segments: the term/gloss items. */
  vocab?: TranscriptVocabItem[];
}

export interface AudioTranscript {
  episodeSlug: string;
  /** Total audio duration including baked pauses, ms. */
  durationMs: number;
  /** Which side the narrator speaks. */
  spokenLanguage: TeachingLanguage;
  segments: AudioTranscriptSegment[];
}

// ── Defensive parsing ─────────────────────────────────────────────────────────

function asNumber(v: unknown, fallback = 0): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : fallback;
}

function asString(v: unknown, fallback = ''): string {
  return typeof v === 'string' ? v : fallback;
}

function asStringRecord(v: unknown): Record<string, string> | undefined {
  if (!v || typeof v !== 'object') return undefined;
  const out: Record<string, string> = {};
  for (const [k, val] of Object.entries(v as Record<string, unknown>)) {
    if (typeof val === 'string') out[k] = val;
  }
  return out;
}

function parseWords(raw: unknown): TranscriptWord[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((w) => {
    const o = (w ?? {}) as Record<string, unknown>;
    return { text: asString(o.text), startMs: asNumber(o.startMs), endMs: asNumber(o.endMs) };
  });
}

function parseSegment(raw: unknown, fallbackIndex: number): AudioTranscriptSegment {
  const o = (raw ?? {}) as Record<string, unknown>;
  const seg: AudioTranscriptSegment = {
    index: asNumber(o.index, fallbackIndex),
    type: asString(o.type),
    role: asString(o.role),
    startMs: asNumber(o.startMs),
    endMs: asNumber(o.endMs),
    pauseAfterMs: asNumber(o.pauseAfterMs),
    text: asString(o.text),
    words: parseWords(o.words),
  };

  const pair = o.transcriptPair as Record<string, unknown> | undefined;
  if (pair && typeof pair === 'object') {
    const native = asStringRecord(pair.native);
    if (native) seg.transcriptPair = { native, target: asString(pair.target) };
  }

  const narration = asStringRecord(o.narration);
  if (narration) seg.narration = narration;

  if (Array.isArray(o.vocab)) {
    seg.vocab = (o.vocab as unknown[])
      .map((item) => {
        const i = (item ?? {}) as Record<string, unknown>;
        const source = asStringRecord(i.source) ?? {};
        return { target: asString(i.target), source };
      })
      .filter((i) => i.target.length > 0);
  }

  return seg;
}

/**
 * Read the opaque `rendition.transcript` object into the narrow client model.
 * Returns `null` when there is no usable timed transcript (episode without a
 * baked sidecar) — callers then hide the transcript area and keep transport
 * working (AUDIO-LESSONS-004 "Transcript absent").
 */
export function parseTranscript(raw: unknown): AudioTranscript | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  if (!Array.isArray(o.segments) || o.segments.length === 0) return null;

  const segments = o.segments.map((s, i) => parseSegment(s, i));
  const spoken = o.spokenLanguage === 'target' ? 'target' : 'source';

  return {
    episodeSlug: asString(o.episodeSlug),
    durationMs: asNumber(o.durationMs),
    spokenLanguage: spoken,
    segments,
  };
}

// ── Position lookups (karaoke) ────────────────────────────────────────────────

/**
 * Index of the last element whose `startMs <= positionMs`, via binary search on
 * a list assumed sorted ascending by `startMs`. Returns -1 when `positionMs`
 * precedes the first element. Shared by the segment and word lookups.
 */
function lastStartAtOrBefore(items: { startMs: number }[], positionMs: number): number {
  let lo = 0;
  let hi = items.length - 1;
  let found = -1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (items[mid].startMs <= positionMs) {
      found = mid;
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }
  return found;
}

/**
 * The index of the active segment for a playback position (ms). Segments are
 * contiguous on the timeline (each followed by its baked pause), so the active
 * line is the last segment that has started. Returns -1 before the first
 * segment so the caller can render nothing-highlighted at position 0.
 */
export function findActiveSegmentIndex(segments: AudioTranscriptSegment[], positionMs: number): number {
  if (segments.length === 0) return -1;
  return lastStartAtOrBefore(segments, positionMs);
}

/**
 * The index of the active word within a segment for a playback position (ms).
 * Prefers the word whose `[startMs, endMs)` window contains the position; if the
 * position sits in an inter-word gap, falls back to the last word that has
 * started. Returns -1 before the segment's first word.
 */
export function findActiveWordIndex(words: TranscriptWord[], positionMs: number): number {
  if (words.length === 0) return -1;
  const idx = lastStartAtOrBefore(words, positionMs);
  return idx;
}

/** Strip to comparable form: lowercase, drop accents + all non-alphanumeric. */
function normWord(s: string): string {
  return s.toLowerCase().normalize('NFD').replace(/[^\p{L}\p{N}]/gu, '');
}

/**
 * Map each SPOKEN display token to a start time by aligning the display tokens to
 * the timed Azure `words` **by text**, not by blind index. Azure's `words[]` can
 * carry extra entries (punctuation) or garbled ones (the known `<lang>` /
 * multilingual word-boundary bug — see docs/project_notes/bugs.md), and a plain
 * index map lets one stray entry desync every later word. Text alignment skips
 * non-matching entries (within a small window) so the highlight self-corrects.
 *
 * Returns a non-decreasing `startMs` per spoken token. When the texts can't be
 * aligned at all (a fully garbled segment), it spreads tokens evenly across the
 * segment so the karaoke still advances roughly instead of freezing/jumping.
 */
export function alignTokenTimes(
  spokenTokens: string[],
  words: TranscriptWord[],
  segStartMs: number,
  segEndMs: number,
): number[] {
  const n = spokenTokens.length;
  if (n === 0) return [];

  const wordNorm = words.map((w) => normWord(w.text));
  const times: (number | null)[] = new Array(n).fill(null);
  let wi = 0;
  let matched = 0;

  for (let ti = 0; ti < n; ti++) {
    const nt = normWord(spokenTokens[ti]);
    if (!nt) continue;
    // Look for this token's word at/after the cursor, within a small window so a
    // stray entry is skipped without stealing a later token's word.
    for (let j = wi; j < words.length && j <= wi + 4; j++) {
      if (wordNorm[j] === nt) {
        times[ti] = words[j].startMs;
        wi = j + 1;
        matched++;
        break;
      }
    }
  }

  // Too few matches → the segment's word timings are unusable (garbled). Spread
  // the tokens evenly so the highlight tracks the line roughly rather than freezing.
  if (matched < Math.max(1, Math.ceil(n / 2))) {
    const span = Math.max(1, segEndMs - segStartMs);
    return spokenTokens.map((_, i) => segStartMs + Math.round((span * i) / n));
  }

  // Forward-fill unmatched tokens so they highlight alongside their preceding word.
  let last = segStartMs;
  for (let i = 0; i < n; i++) {
    if (times[i] == null) times[i] = last;
    else last = times[i] as number;
  }
  return times as number[];
}

/**
 * Index of the last value `<= positionMs` in a non-decreasing array (binary
 * search). Returns -1 when `positionMs` precedes the first value. Pairs with
 * {@link alignTokenTimes} to pick the active token by time.
 */
export function lastIndexAtOrBefore(times: number[], positionMs: number): number {
  let lo = 0;
  let hi = times.length - 1;
  let found = -1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (times[mid] <= positionMs) {
      found = mid;
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }
  return found;
}
